import React from 'react'
import styled from '@emotion/styled'
import { useNavigate } from 'react-router'
import { useQueryClient } from '@tanstack/react-query'
import { toMoney } from '@mobi/utils/money'
import { formatDate } from '@mobi/utils/date'
import { CustomerServiceTelephone } from '@mobi/component-library/Base/Link'
import { Button } from '@mobi/component-library/Base/Buttons'
import { Input } from '@mobi/component-library/Selection&Input/Inputs'
import { InfoBox } from '@mobi/component-library/Feedback/InfoBox'
import { Modal } from '@mobi/component-library/Overlays/Modal'
import { colors, font, spacing } from '@mobi/component-library/Foundations'
import { DropdownInput } from '@mobi/component-library/Selection&Input/DropdownInput'
import { addToast } from '@mobi/component-library/Feedback/Toast'
import type { DepositLimitFrequency, PreCommitmentDetails } from '@mobi/api-types'
import { Spinner } from '@mobi/component-library/Legacy/Spinner'
import { DepositLimitFormStyled, Content, Footer, DepositLimitLegal } from './DepositLimit.styled'
import { validateDepositLimitAmount, DEPOSIT_LIMIT_MIN, DEPOSIT_LIMIT_MAX } from '../Utils'
import { NUMERIC_REGEX } from '@mobi/account/Utils/validation'
import { queryKeys } from '@mobi/account/Utils'
import { initializeWithCountIfNotInitialized } from '@mobi/account/Areas/Deposit/Utils'
import { routes, trackSignUpNoLimitSetConfirmed } from '../../SignUp/Utils'
import {
  DepositLimitAndAnniversaryRequest,
  useSaveDepositLimitAndAnniversary,
} from '../Hooks/useSaveDepositLimitAndAnniversary'
import { trackDepositLimitError, trackNeedAssistance } from '@mobi/account/Common/Analytics'

const depositLimitFrequencies: Exclude<DepositLimitFrequency, null>[] = [
  'Daily',
  'Weekly',
  'Fortnightly',
  'Monthly',
]

type DepositLimitFormProps = {
  onDepositLimitSave: (
    frequency: DepositLimitFrequency | undefined,
    amount: string | undefined
  ) => void
  betAccountNumber: number
  isSignupFlow?: boolean
  precommitmentDetails: Partial<PreCommitmentDetails>
}

export const DepositLimitForm = ({
  onDepositLimitSave,
  precommitmentDetails,
  betAccountNumber,
  isSignupFlow,
}: DepositLimitFormProps) => {
  const navigate = useNavigate()
  const {
    ProposedOptOutDate,
    MaxBetAmount,
    ProposedMaxBetAmount,
    ProposedMaxBetDate,
    OptInMarketingIndicator,
    UserCreditCardBlockedIndicator,
    ProposedUserCreditCardBlockedDate,
    BlockPayPalIndicator,
    ProposedPayPalReleaseDate,
    MaxDepositAmount,
    MaxDepositFrequency,
    ProposedMaxDepositAmount,
    ProposedMaxDepositFrequency,
    ProposedMaxDepositDate,
  } = precommitmentDetails

  const [amount, setAmount] = React.useState(MaxDepositAmount?.toString() ?? '')
  const [frequency, setFrequency] = React.useState<DepositLimitFrequency>(
    MaxDepositFrequency ?? null
  )
  const [amountValidationMessage, setAmountValidationMessage] = React.useState('')
  const [isSignUpOnlyNoDepositLimitWarningOpen, setIsSignUpOnlyNoDepositLimitWarningOpen] =
    React.useState(false)
  const [isCoolingOffConfirmationModalOpen, setIsCoolingOffConfirmationModalOpen] =
    React.useState(false)
  const [hasShownSignUpOnlyNoDepositLimitWarning, setHasShownSignUpOnlyNoDepositLimitWarning] =
    React.useState(false)
  const queryClient = useQueryClient()

  const isFrequencySelected = !!frequency
  const amountNumber = Number(amount)

  const { saveDepositLimitAndAnniversary, saveDepositLimitAndAnniversaryStatus } =
    useSaveDepositLimitAndAnniversary()

  const onSubmit = () => {
    const newMaxBetAmount =
      ProposedMaxBetDate || ProposedOptOutDate ? ProposedMaxBetAmount : MaxBetAmount

    const preCommitmentDetails: DepositLimitAndAnniversaryRequest = {
      betAccountNumber: betAccountNumber,
      optInMarketingIndicator: OptInMarketingIndicator ?? false,
      preCommitmentMaxDepositAmount: isFrequencySelected ? amountNumber : undefined,
      preCommitmentMaxDepositFrequency: isFrequencySelected ? frequency : undefined,
      // The CRM API requires these transformations or it will delete any proposed values
      maxBetAmount: newMaxBetAmount,
      userCreditCardBlockedIndicator:
        !ProposedUserCreditCardBlockedDate && UserCreditCardBlockedIndicator,
      blockPayPalIndicator: !ProposedPayPalReleaseDate && BlockPayPalIndicator,
    }

    saveDepositLimitAndAnniversary(preCommitmentDetails, {
      onSuccess: () => {
        onDepositLimitSave(frequency, amount)
        addToast({
          id: 'deposit-limit-success',
          isDismissable: true,
          title: 'Your deposit limit has been set.',
          icon: 'success',
        })
        queryClient.invalidateQueries({ queryKey: [queryKeys.depositInitialData] })
        queryClient.invalidateQueries({ queryKey: [queryKeys.accountHolders] })
        queryClient.invalidateQueries({ queryKey: [queryKeys.betAccount] }) // closes deposit limit anniversary pop up
        initializeWithCountIfNotInitialized(betAccountNumber, 0)
        if (isSignupFlow) navigate(routes.signUpComplete)
      },
      onError: () => trackDepositLimitError('Deposit limit error'),
    })
  }

  const onClickSave = () => {
    // Only show the no limit warning modal once
    const shouldShowSignUpOnlyNoLimitWarning =
      isSignupFlow && !isFrequencySelected && !hasShownSignUpOnlyNoDepositLimitWarning
    const shouldShowCoolingOffConfirmation = !isSignupFlow

    if (!shouldShowSignUpOnlyNoLimitWarning && !shouldShowCoolingOffConfirmation) {
      onSubmit()
      return
    }

    if (shouldShowSignUpOnlyNoLimitWarning) {
      setIsSignUpOnlyNoDepositLimitWarningOpen(true)
      setHasShownSignUpOnlyNoDepositLimitWarning(true)
    }
    if (shouldShowCoolingOffConfirmation) {
      setIsCoolingOffConfirmationModalOpen(true)
    }
  }

  const handleAmountChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = event.target.value
    const sanitizedInputValue = inputValue.replace(NUMERIC_REGEX, '')
    setAmount(sanitizedInputValue)
    setAmountValidationMessage(validateDepositLimitAmount(sanitizedInputValue))
  }

  const ErrorInfoBox = () => {
    return (
      <InfoBox color='red' size='md'>
        <b>Deposit Limit Error</b>
        <div>
          Sorry, a network issue has prevented us from updating your deposit limit. Please try
          again, or contact us on{' '}
          <CustomerServiceTelephone onClick={() => trackNeedAssistance('Deposit Limit')} />{' '}
        </div>
      </InfoBox>
    )
  }

  const PendingDepositLimitInfoBox = () => {
    const isLimitPending = !!(ProposedMaxDepositDate || ProposedOptOutDate)
    if (!isLimitPending) return null

    const isOptingOut = !!(
      ProposedOptOutDate ||
      (ProposedMaxDepositDate && !ProposedMaxDepositFrequency && !ProposedMaxDepositAmount) ||
      (!MaxDepositFrequency &&
        !ProposedMaxDepositFrequency &&
        !MaxDepositAmount &&
        !ProposedMaxDepositAmount)
    )

    const pendingLimitMessage = isOptingOut
      ? 'Your deposit limit will be removed on the '
      : `Your ${ProposedMaxDepositFrequency} deposit limit of ${toMoney(ProposedMaxDepositAmount ?? 0, { decimalPlaces: 0 })} will be effective on the `

    return (
      <InfoBox color='orange' size='md'>
        <div>
          {pendingLimitMessage}
          <strong>{formatDate(ProposedMaxDepositDate || ProposedOptOutDate, 'DD MMM YYYY')}</strong>
          , after the mandatory 7 day cooling off period.
        </div>
      </InfoBox>
    )
  }

  const FrequencyInfoBox = () => {
    let frequencyHelpText = null
    switch (frequency) {
      case 'Daily':
        frequencyHelpText = 'Resets at 12:00am each day'
        break
      case 'Weekly':
        frequencyHelpText = 'Mon-Sun, resets at 12:00am each Monday'
        break
      case 'Fortnightly':
        frequencyHelpText = 'Mon-Sun, resets at 12:00am every second Monday'
        break
      case 'Monthly':
        frequencyHelpText = 'Resets at 12:00am on the first day of each month'
        break
      default:
        frequencyHelpText = null
        break
    }

    if (!frequencyHelpText) return null
    return (
      <InfoBox color='blue' size='sm' style={{ marginTop: spacing.smx1 }}>
        {frequencyHelpText}
      </InfoBox>
    )
  }

  const isSaveButtonDisabled = () => {
    // Signup flow with no frequency allows submitting to continue without limit
    if (!isFrequencySelected) return false

    const hasFrequencyChanged = frequency !== (ProposedMaxDepositFrequency ?? MaxDepositFrequency)
    const hasAmountChanged = amountNumber !== (ProposedMaxDepositAmount ?? MaxDepositAmount)
    const hasLimitChanged = hasFrequencyChanged || hasAmountChanged

    const isAmountInvalid =
      isNaN(amountNumber) ||
      amountNumber < DEPOSIT_LIMIT_MIN ||
      amountNumber > DEPOSIT_LIMIT_MAX ||
      !Number.isInteger(amountNumber)

    return !hasLimitChanged || isAmountInvalid
  }

  return (
    <DepositLimitFormStyled>
      {saveDepositLimitAndAnniversaryStatus === 'pending' && <Spinner />}
      {saveDepositLimitAndAnniversaryStatus === 'error' && <ErrorInfoBox />}
      <Content>
        <PendingDepositLimitInfoBox />

        <div>
          <DropdownInput
            label='Limit Frequency'
            name='deposit-limit-frequency'
            value={frequency || undefined}
            onChange={event => {
              setFrequency((event.target.value || null) as DepositLimitFrequency)
            }}
          >
            <option value=''>No Personal Limit</option>
            {depositLimitFrequencies.map(freq => (
              <option key={freq} value={freq}>
                {freq}
              </option>
            ))}
          </DropdownInput>
          <FrequencyInfoBox />
        </div>

        <Input
          label='Limit Amount'
          name='deposit-limit-amount'
          helpText={
            !isSignupFlow || amountValidationMessage
              ? amountValidationMessage
              : 'Any future increases to your deposit limit will include a 7 day cooling off period.'
          }
          prefix={amount.length > 0 && '$'}
          type='text'
          min={DEPOSIT_LIMIT_MIN}
          max={DEPOSIT_LIMIT_MAX}
          maxLength={DEPOSIT_LIMIT_MAX.toFixed(0).length}
          value={isFrequencySelected ? amount : ''}
          onChange={handleAmountChange}
          isInvalid={isFrequencySelected && !!amountValidationMessage}
          isDisabled={!isFrequencySelected}
          placeholder={isFrequencySelected ? 'Please enter an amount' : 'N/A'}
          inputMode='numeric'
        />
      </Content>
      <Footer>
        <Button
          color='primary'
          isFullWidth
          size='md'
          disabled={isSaveButtonDisabled()}
          onClick={onClickSave}
        >
          Set deposit limit
        </Button>
      </Footer>

      <DepositLimitLegal>
        TABtouch supports responsible wagering. You can control the amount you deposit by setting
        your preferred limits on this page.
        <br />
        <br />
        Please be aware there is a standard daily limit of $10,000 for debit card, Apple Pay, Google
        Pay and PayPal deposits.
      </DepositLimitLegal>

      <SignUpOnlyNoDepositLimitWarning
        isOpen={isSignUpOnlyNoDepositLimitWarningOpen}
        onConfirm={() => {
          if (isSignupFlow) trackSignUpNoLimitSetConfirmed()
          onSubmit()
          setIsSignUpOnlyNoDepositLimitWarningOpen(false)
        }}
        onCancel={() => setIsSignUpOnlyNoDepositLimitWarningOpen(false)}
      />
      <CoolingOffConfirmationModal
        isOpen={isCoolingOffConfirmationModalOpen}
        onConfirm={() => {
          onSubmit()
          setIsCoolingOffConfirmationModalOpen(false)
        }}
        onCancel={() => setIsCoolingOffConfirmationModalOpen(false)}
      />
    </DepositLimitFormStyled>
  )
}

type SignUpOnlyNoDepositLimitWarningProps = {
  isOpen: boolean
  onConfirm: () => void
  onCancel: () => void
}

const SignUpOnlyNoDepositLimitWarning = ({
  isOpen,
  onConfirm,
  onCancel,
}: SignUpOnlyNoDepositLimitWarningProps) => (
  <Modal
    headerIcon='confirm'
    headerIconAlignment='left'
    headerTitle='Are you sure?'
    headerSubtitle='Deposit limit can help you proactively control your online wagering by:'
    headerTextAlignment='left'
    isOpen={isOpen}
    dismissStatus='disallowed'
    footerComponent={
      <ModalFooterStyled>
        <Button isFullWidth color='primary' onClick={onConfirm}>
          Continue without deposit limit
        </Button>

        <Button isFullWidth color='secondary_color' onClick={onCancel}>
          Set deposit limit
        </Button>
      </ModalFooterStyled>
    }
  >
    <ConfirmModalContent>
      <ol>
        <li>Assisting with tracking your expenditure</li>
        <li>Removing the temptation to spend more and chase losses</li>
        <li>Limiting your daily wagering</li>
      </ol>
    </ConfirmModalContent>
  </Modal>
)

type CoolingOffConfirmationModalProps = {
  isOpen: boolean
  onConfirm: () => void
  onCancel: () => void
}

const CoolingOffConfirmationModal = ({
  isOpen,
  onConfirm,
  onCancel,
}: CoolingOffConfirmationModalProps) => (
  <Modal
    headerIcon='warning'
    headerIconAlignment='center'
    headerTitle='Confirm Deposit Limit'
    headerSubtitle='I understand that once my deposit limit is set, there will be a 7 day cooling off period should I choose to increase the amount or frequency of the limit. Any limit decrease will be effective immediately.'
    headerTextAlignment='center'
    isOpen={isOpen}
    dismissStatus='disallowed'
    footerComponent={
      <ModalFooterStyled>
        <Button isFullWidth color='primary' onClick={onConfirm}>
          Confirm
        </Button>

        <Button isFullWidth color='secondary_color' onClick={onCancel}>
          Cancel
        </Button>
      </ModalFooterStyled>
    }
  />
)

// ======
// Styles
// ======

const ModalFooterStyled = styled.div({
  display: 'flex',
  flexDirection: 'column',
  gap: spacing.smx1,
  width: '100%',
})

const ConfirmModalContent = styled.div({
  color: colors.neutral[800],

  '> ol': {
    textAlign: 'left',
    marginBottom: spacing.bigx1,
    marginTop: spacing.none,
    paddingLeft: font.size.lg.lineHeight,

    '> li': {
      ...font.size.lg,
      lineHeight: font.size.xl.lineHeight,
    },
  },
})
